<template>
  <div class="pagination-container">
    <div class="pagination">
      <!-- 上一页 -->
      <button 
        class="pagination-btn prev" 
        :disabled="page === 1"
        @click="handlePageChange(page - 1)"
      >
        上一页
      </button>
      
      <!-- 页码区域 -->
      <div class="pagination-pages">
        <!-- 第一页 -->
        <button 
          class="pagination-page" 
          :class="{ active: page === 1 }"
          @click="handlePageChange(1)"
        >
          1
        </button>
        
        <!-- 前省略号 -->
        <span 
          class="pagination-ellipsis" 
          v-if="showStartEllipsis"
        >
          ...
        </span>
        
        <!-- 中间页码 -->
        <button 
          v-for="pageNum in middlePages" 
          :key="pageNum"
          class="pagination-page" 
          :class="{ active: page === pageNum }"
          @click="handlePageChange(pageNum)"
        >
          {{ pageNum }}
        </button>
        
        <!-- 后省略号 -->
        <span 
          class="pagination-ellipsis" 
          v-if="showEndEllipsis"
        >
          ...
        </span>
        
        <!-- 最后一页 -->
        <button 
          v-if="totalPage > 1 && page !== totalPage"
          class="pagination-page" 
          :class="{ active: page === totalPage }"
          @click="handlePageChange(totalPage)"
        >
          {{ totalPage }}
        </button>
      </div>
      
      <!-- 下一页 -->
      <button 
        class="pagination-btn next" 
        :disabled="page === totalPage"
        @click="handlePageChange(page + 1)"
      >
        下一页
      </button>
    </div>
    
    <!-- 页面信息 -->
    <div class="pagination-info" v-if="showInfo">
      共 {{ totalSize }} 条，第 {{ page }} 页 / 共 {{ totalPage }} 页
    </div>
  </div>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    // 当前页码
    page: {
      type: Number,
      required: true,
      default: 1
    },
    // 每页显示数量
    limit: {
      type: Number,
      required: true,
      default: 10
    },
    // 总数据量
    totalSize: {
      type: Number,
      required: true,
      default: 0
    },
    // 总页数
    totalPage: {
      type: Number,
      required: true,
      default: 1
    },
    // 中间显示的页码数量
    pageCount: {
      type: Number,
      default: 5
    },
    // 是否显示页面信息
    showInfo: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    // 是否显示前省略号
    showStartEllipsis() {
      return this.page > 4 && this.totalPage > 10;
    },
    
    // 是否显示后省略号
    showEndEllipsis() {
      return this.page < this.totalPage - 3 && this.totalPage > 10;
    },
    
    // 中间显示的页码数组
    middlePages() {
      const pages = [];
      
      // 如果总页数小于等于10，显示所有页码
      if (this.totalPage <= 10) {
        for (let i = 2; i < this.totalPage; i++) {
          pages.push(i);
        }
        return pages;
      }
      
      const half = Math.floor(this.pageCount / 2);
      
      let start = Math.max(2, this.page - half);
      let end = Math.min(this.totalPage - 1, this.page + half);
      
      // 调整起始位置，确保显示指定数量的页码
      if (this.page <= half + 1) {
        end = Math.min(this.totalPage - 1, this.pageCount + 1);
      }
      
      if (this.page >= this.totalPage - half) {
        start = Math.max(2, this.totalPage - this.pageCount);
      }
      
      // 生成页码数组
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },
  methods: {
    // 处理页码变化
    handlePageChange(newPage) {
      if (newPage < 1 || newPage > this.totalPage || newPage === this.page) {
        return;
      }
      
      this.$emit('update:page', newPage);
      this.$emit('page-change', newPage);
    }
  },
  watch: {
    // 监听总页数变化，确保当前页不超出范围
    totalPage(newTotalPage) {
      if (this.page > newTotalPage) {
        this.handlePageChange(newTotalPage);
      }
    }
  }
}
</script>

<style scoped>
.pagination-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin: 20px 0;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 5px;
  border: 1px solid #dcdfe6;
  font-family: 'Comic Sans MS', cursive;
  background-color: rgba(255, 255, 255, 0) !important;
  color: #FFFFFF;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
}

.pagination-btn:hover:not(:disabled) {
  color: #FFFFFF;
  border-color: #FFFFFF;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  gap: 4px;
}

.pagination-page {
  min-width: 30px;
  height: 40px;
  padding: 0 5px;
  border: 1px solid #dcdfe6;
  font-family: 'Comic Sans MS', cursive;
  background-color: rgba(255, 255, 255, 0) !important;
  color: var(--font-color-1);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-page:hover:not(.active) {
  color: #FFFFFF;
  font-family: 'Comic Sans MS', cursive;
  background-color: #f2e8bf;
}

.pagination-page.active {
  background: #FFFFFF;
  font-family: 'Comic Sans MS', cursive;
  background-color: #f2e8bf;
  color: var(--font-color-2);
}

.pagination-ellipsis {
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 14px;
}

.pagination-info {
  font-size: 14px;
  color: var(--font-color-1);
}
</style>